/* RapidPlas delivery locator — Golden Panda / SF035
   Informational only. Never disables or blocks Add to Cart.
   Flow: suburb search -> zone -> (zone x shipping class) -> freight outcome.
   Selection is remembered locally and written to cart attributes. */
(function () {
  'use strict';

  var STORE_KEY = 'rp_delivery_location';
  var MAX_RESULTS = 10;
  var MIN_CHARS = 2;

  var money = function (n) {
    return '$' + Number(n).toLocaleString('en-AU', {
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2
    });
  };

  var COPY = {
    free: 'Free delivery to your area.',
    priced: function (amount, qty) {
      return qty > 1
        ? 'Delivery to your area: ' + money(amount) + ' for ' + qty + ' items (incl. GST).'
        : 'Delivery to your area: ' + money(amount) + ' (incl. GST).';
    },
    unavailable: 'Sorry, this product isn\u2019t available for online purchase in your area just yet. Request a quote or find your nearest dealer.',
    restricted: 'Due to the item\u2019s size, delivery to your location may require specialized road permits or an escort vehicle. Please reach out to our sales team to finalize logistics and pricing.',
    unknownRate: 'We can deliver this item to your area. Delivery pricing is calculated at checkout.',
    noMatch: 'We couldn\u2019t find that suburb. Try the postcode, or contact our sales team.'
  };

  function fetchJSON(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, ms);
    };
  }

  function Locator(root) {
    this.root = root;
    this.input = root.querySelector('[data-rp-input]');
    this.results = root.querySelector('[data-rp-results]');
    this.outcome = root.querySelector('[data-rp-outcome]');
    this.clearBtn = root.querySelector('[data-rp-clear]');
    this.restricted = (root.dataset.restrictedClasses || '')
      .split(',').map(function (s) { return parseInt(s, 10); })
      .filter(function (n) { return !isNaN(n); });

    try {
      var tag = root.querySelector('[data-rp-variant-classes]');
      this.variantClasses = tag ? JSON.parse(tag.textContent) : {};
    } catch (e) { this.variantClasses = {}; }

    // SKU -> class. The theme exposes no input[name="id"] and does not keep
    // ?variant= in sync, so the selected variant cannot be read from the form
    // or the URL. It IS printed on the page as the variant's Code line, which
    // updates reliably, so match that against this known list instead.
    try {
      var skuTag = root.querySelector('[data-rp-variant-skus]');
      this.variantSkus = skuTag ? JSON.parse(skuTag.textContent) : {};
    } catch (e) { this.variantSkus = {}; }
    this._skuLookup = null;

    this.zones = null;
    this.rates = null;
    this.matches = [];
    this.activeIndex = -1;
    this.selection = null;
    this._lastHtml = null;

    this.bind();
    this.restore();
  }

  Locator.prototype.load = function () {
    if (this._loading) return this._loading;
    var self = this;
    this._loading = Promise.all([
      fetchJSON(this.root.dataset.zonesUrl),
      fetchJSON(this.root.dataset.ratesUrl).catch(function () { return null; })
    ]).then(function (res) {
      self.zones = res[0];
      self.rates = res[1];
    });
    return this._loading;
  };

  /* ---------- current variant + quantity ---------- */

  Locator.prototype.currentVariantId = function () {
    // The form's own value comes first. The URL's ?variant= can go stale when
    // the shopper switches back to the product's default variant — the theme
    // does not always rewrite it — which left the previous variant's freight
    // on screen (145L -> 50L kept showing the 145L rate).
    var input = document.querySelector('input[name="id"], select[name="id"]');
    if (input && input.value && this.variantClasses[input.value] != null) return input.value;
    var url = new URL(window.location.href);
    var v = url.searchParams.get('variant');
    if (v && this.variantClasses[v] != null) return v;
    var keys = Object.keys(this.variantClasses);
    return keys.length ? keys[0] : null;
  };

  // Read the variant's Code line off the page and map it to a shipping class.
  // Takes the last match: the product-level code is printed above the variant
  // one, and the variant code is what changes when the shopper picks a size.
  Locator.prototype.classFromSku = function () {
    if (!this.variantSkus) return 0;
    if (!this._skuLookup) {
      this._skuLookup = {};
      var ks = Object.keys(this.variantSkus);
      for (var i = 0; i < ks.length; i++) {
        if (ks[i]) this._skuLookup[ks[i].trim().toUpperCase()] = this.variantSkus[ks[i]];
      }
    }
    // Scan the whole document. The quantity picker turned out to sit outside
    // product-main / .js-product-main at desktop width, and the Code line is
    // outside them too, so a scoped search never saw it. The SKU set is small
    // and unique, so a document-wide match is unambiguous.
    var els = document.querySelectorAll('span, div, p, small, strong, b, em, li, td, dd');
    var found = 0;
    for (var j = 0; j < els.length; j++) {
      var el = els[j];
      if (el.offsetParent === null) continue;
      // Read the element's OWN text nodes, not its whole subtree. The Code line
      // is rendered as <p><span>Code</span> PTT12WB</p>: the SKU is a bare text
      // node beside a child element, so neither a leaf-only scan nor a
      // textContent match on the parent ever sees it on its own.
      var own = '';
      for (var c = 0; c < el.childNodes.length; c++) {
        if (el.childNodes[c].nodeType === 3) own += el.childNodes[c].nodeValue;
      }
      var t = own.trim().toUpperCase().replace(/^CODE\s+/, '');
      if (t && this._skuLookup[t] != null) found = parseInt(this._skuLookup[t], 10) || 0;
    }
    return found;
  };

  Locator.prototype.currentClass = function () {
    var fromSku = this.classFromSku();
    if (fromSku > 0) return fromSku;
    var id = this.currentVariantId();
    var cls = id ? this.variantClasses[id] : 0;
    return parseInt(cls, 10) || 0;
  };

  // A quantity picker's options are consecutive integers starting at 1. Product
  // option selects can also be all-numeric (Pro Tub Troughs has Capacity: 1200),
  // so "all numeric" alone matched the wrong dropdown and priced 1200 items.
  function isQuantitySelect(s) {
    if (!s.options || !s.options.length) return false;
    for (var i = 0; i < s.options.length; i++) {
      var t = (s.options[i].textContent || '').trim();
      if (!/^\d+$/.test(t)) return false;
      if (parseInt(t, 10) !== i + 1) return false;
    }
    return true;
  }

  // The shopper's own interaction is the most reliable source: whichever
  // quantity select they actually change, we record it. This sidesteps every
  // guess about which duplicate is visible or which comes first in the DOM —
  // the theme renders the product column once per breakpoint.
  var lastUserQty = 0;
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || el.tagName !== 'SELECT' || !isQuantitySelect(el)) return;
    var q = parseInt(el.value, 10);
    if (q > 0) lastUserQty = q;
  }, true);

  // Last resort, and the one that matters at desktop width: the theme renders
  // the quantity as a custom widget with no <select> at all, so there is
  // nothing to read by element type. Find the visible Add to Cart / Add to
  // Quote button and take the bare number displayed beside it in the same row.
  function qtyFromWidget() {
    var btn = null;
    var bs = document.querySelectorAll('button, [type="submit"]');
    for (var i = 0; i < bs.length; i++) {
      if (bs[i].offsetParent === null) continue;
      var t = (bs[i].textContent || '').trim().toLowerCase();
      // Out-of-stock products still show a quantity picker, and the button text
      // changes, so match those labels too or the widget is never found.
      if (t === 'add to cart' || t === 'add to quote' || t === 'out of stock' ||
          t === 'sold out' || t === 'unavailable') { btn = bs[i]; break; }
    }
    if (!btn) return 0;

    var node = btn;
    for (var up = 0; up < 4 && node; up++) {
      node = node.parentNode;
      if (!node || !node.querySelectorAll) break;
      var kids = node.querySelectorAll('*');
      for (var k = 0; k < kids.length; k++) {
        var el = kids[k];
        if (el === btn || btn.contains(el) || el.contains(btn)) continue;
        if (el.children.length) continue;          // leaf nodes only
        if (el.offsetParent === null) continue;    // must be on screen
        var txt = (el.textContent || '').trim();
        if (/^\d{1,3}$/.test(txt)) {
          var q = parseInt(txt, 10);
          if (q > 0) return q;
        }
      }
    }
    return 0;
  }

  // Visible wins. product-main renders the product column twice — once for
  // mobile, once for desktop — so at desktop width there are two quantity
  // selects and the first in DOM order is the hidden mobile one, still at 1.
  // Reading that one showed qty 1 pricing while the shopper saw 2 selected.
  function pickQty(nodes) {
    var hidden = 0;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.tagName === 'SELECT' && !isQuantitySelect(el)) continue;
      var q = parseInt(el.value, 10);
      if (!(q > 0)) continue;
      // offsetParent is null for display:none subtrees, which is how the theme
      // hides the breakpoint that does not apply.
      if (el.offsetParent === null) { if (!hidden) hidden = q; continue; }
      return q;
    }
    return hidden;
  }

  Locator.prototype.currentQty = function () {
    // What the shopper last chose wins outright.
    if (lastUserQty > 0) return lastUserQty;
    // Otherwise match on name, then a select shaped like a quantity picker.
    var named = pickQty(document.querySelectorAll('[name="quantity"], [data-quantity], [data-quantity-input]'));
    if (named > 0) return named;
    // Scan every select on the page, not just those inside product-form or
    // .js-product-main: at desktop width the quantity picker sits outside both,
    // so the scoped search found nothing and silently defaulted to 1.
    // isQuantitySelect (consecutive integers from 1) is what keeps colour,
    // capacity and locale selects out.
    var fromSelect = pickQty(document.querySelectorAll('select'));
    if (fromSelect > 0) return fromSelect;
    return qtyFromWidget() || 1;
  };

  /* ---------- search ---------- */

  Locator.prototype.search = function (termRaw) {
    var term = termRaw.trim().toLowerCase();
    if (!this.zones || term.length < MIN_CHARS) return [];
    var out = [];
    var pcs = this.zones.postcodes;
    var zl = this.zones.zones;
    var isNum = /^\d+$/.test(term);

    if (isNum) {
      for (var pc in pcs) {
        if (pc.indexOf(term) !== 0) continue;
        var list = pcs[pc];
        for (var i = 0; i < list.length; i++) {
          out.push(this.entry(pc, list[i], zl));
          if (out.length >= MAX_RESULTS * 3) break;
        }
        if (out.length >= MAX_RESULTS * 3) break;
      }
      out.sort(function (a, b) {
        return a.postcode.localeCompare(b.postcode) || a.suburb.localeCompare(b.suburb);
      });
    } else {
      // Match the start of the name, or the start of any word within it.
      // "springfield" -> SPRINGFIELD, SPRINGFIELD LAKES; "heads" -> BRUNSWICK HEADS.
      // "nsw" matches nothing, rather than BRU-NSW-ICK.
      var starts = [], contains = [];
      for (var pc2 in pcs) {
        var l2 = pcs[pc2];
        for (var j = 0; j < l2.length; j++) {
          var name = l2[j][0].toLowerCase();
          if (name.indexOf(term) === 0) {
            starts.push(this.entry(pc2, l2[j], zl));
          } else {
            var words = name.split(/[\s\-']+/);
            for (var w = 1; w < words.length; w++) {
              if (words[w].indexOf(term) === 0) { contains.push(this.entry(pc2, l2[j], zl)); break; }
            }
          }
        }
      }
      var cmp = function (a, b) {
        return a.suburb.localeCompare(b.suburb) || a.postcode.localeCompare(b.postcode);
      };
      starts.sort(cmp); contains.sort(cmp);
      out = starts.concat(contains);
    }
    return out.slice(0, MAX_RESULTS);
  };

  Locator.prototype.entry = function (pc, row, zoneList) {
    var zone = zoneList[row[1]];
    return {
      postcode: pc,
      suburb: row[0],
      zone: zone,
      zoneIndex: row[1],
      state: zone.split(' - ')[0],
      excluded: row.length > 2 && row[2] === 1
    };
  };

  /* ---------- outcome ---------- */

  Locator.prototype.resolve = function (sel) {
    var cls = this.currentClass();
    var qty = this.currentQty();

    if (!cls) return { kind: 'unknown', text: COPY.unknownRate };

    // Order matters. Check the zone x class rate first: if the product is N/A
    // for this zone it is simply not available, and offering a permit
    // conversation would be wrong. The permit message is only for suburbs
    // carved out of a zone we DO deliver to.
    var table = (this.rates && this.rates.rates) ? this.rates.rates[String(cls)] : null;
    var hasCell = !!table && (String(sel.zoneIndex) in table);
    var cell = hasCell ? table[String(sel.zoneIndex)] : undefined;

    if (hasCell && cell === null) return { kind: 'unavailable', text: COPY.unavailable };

    if (sel.excluded && this.restricted.indexOf(cls) !== -1) {
      return { kind: 'restricted', text: COPY.restricted };
    }

    if (!hasCell) return { kind: 'unknown', text: COPY.unknownRate };

    var base = cell[0], pct = cell[1] || 0;
    var total = qty > 1 ? base * (1 + pct * (qty - 1)) : base;
    if (total === 0) return { kind: 'free', text: COPY.free };
    return { kind: 'priced', text: COPY.priced(total, qty), amount: total };
  };

  /* ---------- rendering ---------- */

  Locator.prototype.renderResults = function () {
    var self = this;
    this.results.innerHTML = '';
    if (!this.matches.length) { this.hideResults(); return; }

    this.matches.forEach(function (m, i) {
      var li = document.createElement('li');
      li.className = 'rp-locator__result' + (i === self.activeIndex ? ' is-active' : '');
      li.setAttribute('role', 'option');
      li.setAttribute('id', 'rp-opt-' + i);
      li.setAttribute('aria-selected', i === self.activeIndex ? 'true' : 'false');
      li.textContent = self.label(m);
      li.addEventListener('mousedown', function (e) { e.preventDefault(); self.select(m); });
      self.results.appendChild(li);
    });
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  };

  Locator.prototype.hideResults = function () {
    this.results.hidden = true;
    this.activeIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
  };

  Locator.prototype.label = function (m) {
    return (m.suburb + ', ' + m.state + ' ' + m.postcode).toUpperCase();
  };

  Locator.prototype.escape = function (s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  Locator.prototype.showOutcome = function () {
    if (!this.selection) { this.outcome.hidden = true; this._lastHtml = null; return; }
    var r = this.resolve(this.selection);
    var cls = 'rp-locator__outcome rp-locator__outcome--' + r.kind;
    var html =
      '<span class="rp-locator__outcome-place">' +
        this.escape(this.selection.suburb) + ', ' + this.escape(this.selection.state) + ' ' + this.escape(this.selection.postcode) +
      '</span>' +
      '<span class="rp-locator__outcome-text">' + r.text + '</span>';

    if (html === this._lastHtml && this.outcome.className === cls && !this.outcome.hidden) return;

    this._lastHtml = html;
    this.outcome.className = cls;
    this.outcome.innerHTML = html;
    this.outcome.hidden = false;
  };

  /* ---------- selection ---------- */

  Locator.prototype.select = function (m) {
    this.selection = m;
    this.input.value = this.label(m);
    this.clearBtn.hidden = false;
    this.hideResults();
    this.showOutcome();
    this.persist(m);
    this.writeCartAttributes(m);
    document.dispatchEvent(new CustomEvent('rp:delivery-location', { detail: m }));
  };

  Locator.prototype.clear = function () {
    this.selection = null;
    this.input.value = '';
    this.clearBtn.hidden = true;
    this.outcome.hidden = true;
    this.outcome.className = 'rp-locator__outcome';
    this.outcome.innerHTML = '';
    this._lastHtml = null;
    this.hideResults();
    try { localStorage.removeItem(STORE_KEY); } catch (e) {}
  };

  Locator.prototype.persist = function (m) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(m)); } catch (e) {}
  };

  // Every online order is delivered by RapidPlas from the factory; the dealer
  // route is an offline Stockist handoff and the large-tank dealer rebate is
  // allocated internally from the postcode. So the method is constant, but it
  // is recorded on the order for Klaviyo flows and reporting (SF035 scope).
  var FULFILMENT_METHOD = 'RapidPlas direct delivery';

  Locator.prototype.writeCartAttributes = function (m) {
    var body = {
      attributes: {
        'Delivery suburb': m.suburb,
        'Delivery postcode': m.postcode,
        'Delivery state': m.state,
        'Delivery zone': m.zone,
        'Fulfilment method': FULFILMENT_METHOD
      }
    };
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).catch(function () { /* non-blocking */ });
  };

  Locator.prototype.restore = function () {
    var saved;
    try { saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch (e) { saved = null; }
    if (!saved || !saved.postcode) return;
    var self = this;
    this.load().then(function () {
      // re-resolve against current data so a zone change is picked up
      var list = self.zones.postcodes[saved.postcode] || [];
      for (var i = 0; i < list.length; i++) {
        if (list[i][0].toLowerCase() === String(saved.suburb).toLowerCase()) {
          self.selection = self.entry(saved.postcode, list[i], self.zones.zones);
          self.input.value = self.label(self.selection);
          self.clearBtn.hidden = false;
          self.showOutcome();
          return;
        }
      }
    });
  };

  /* ---------- events ---------- */

  Locator.prototype.bind = function () {
    var self = this;

    var onType = debounce(function () {
      var term = self.input.value;
      if (term.trim().length < MIN_CHARS) { self.hideResults(); return; }
      self.load().then(function () {
        self.matches = self.search(term);
        self.activeIndex = -1;
        if (!self.matches.length) {
          self.results.innerHTML = '<li class="rp-locator__result rp-locator__result--empty">' + COPY.noMatch + '</li>';
          self.results.hidden = false;
        } else {
          self.renderResults();
        }
      });
    }, 120);

    this.input.addEventListener('focus', function () { self.load(); });
    this.input.addEventListener('input', function () {
      if (self.selection) {
        self.selection = null;
        self.outcome.hidden = true;
        self.outcome.innerHTML = '';
        self._lastHtml = null;
      }
      self.clearBtn.hidden = !self.input.value;
      onType();
    });

    this.input.addEventListener('keydown', function (e) {
      if (self.results.hidden || !self.matches.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        var dir = e.key === 'ArrowDown' ? 1 : -1;
        self.activeIndex = (self.activeIndex + dir + self.matches.length) % self.matches.length;
        self.renderResults();
      } else if (e.key === 'Enter') {
        if (self.activeIndex >= 0) { e.preventDefault(); self.select(self.matches[self.activeIndex]); }
      } else if (e.key === 'Escape') {
        self.hideResults();
      }
    });

    document.addEventListener('click', function (e) {
      if (!self.root.contains(e.target)) self.hideResults();
    });

    this.clearBtn.addEventListener('click', function () { self.clear(); self.input.focus(); });

    // Re-resolve when the shopper changes variant or quantity.
    // The theme renders these with Preact, so watch rather than bind directly.
    var refresh = debounce(function () { if (self.selection) self.showOutcome(); }, 150);
    // Any change or click on the page re-resolves; cheap because it is debounced
    // and showOutcome only runs when a suburb is already selected.
    document.addEventListener('change', refresh, true);
    document.addEventListener('input', refresh, true);
    document.addEventListener('click', refresh, true);
    window.addEventListener('popstate', refresh);

    var watch = document.querySelector('product-main') || document.querySelector('product-form');
    if (watch && 'MutationObserver' in window) {
      new MutationObserver(refresh).observe(watch, { subtree: true, childList: true, attributes: true });
    }

    // Timing-proof fallback. The theme's custom variant and quantity dropdowns
    // update their underlying value *after* the click/change event fires, so
    // reading on the event alone can pick up the previous value. Re-resolve on a
    // timer instead. showOutcome is idempotent and writes only on a real change,
    // so calling it unconditionally costs nothing and cannot go stale.
    setInterval(function () {
      if (self.selection) self.showOutcome();
    }, 400);
  };

  function init() {
    document.querySelectorAll('[data-rp-locator]').forEach(function (el) {
      if (!el.__rpLocator) el.__rpLocator = new Locator(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
